import { Autocomplete, Box, Grid, MenuItem, TextField } from "@mui/material";
import { Form, Formik } from "formik";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../../app/api/axiosError";
import { updateUserTokenToInvalid } from "../../../../app/features/login/loginSlice";
import {
  RootState,
  useAppDispatch,
} from "../../../../app/features/store/store";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import { RATE_CARD_TYPES } from "../RateCardConstants";
import { RatecardResponse } from "../dtos/RateCardResponse";
import { useSubmitModal } from "../../../../components/common/modalPopup/useSubmitModal";
import UpdateRouteRateModal from "../routeRate/UpdateRouteRateModal";
import { useEffect, useState } from "react";
import {
  fetchStoreRateCardAssociationList,
  updateRatecard,
} from "../../../../app/features/ratecard/ratecardSlice";
import { ParcelCenterRateCardAssociationResponse } from "../dtos/StoreRatecardAssociationResponse";
import {
  UpdateRateCardRequest,
  UpdateRatecardClientMapping,
} from "../dtos/UpdateRateCardRequest";
import {
  getStoreMappingFromStoreName,
  mapClientMappingsResponseToStringList,
} from "../utils/RatecardUtils";
import Loader from "../../../../components/common/loader/Loader";
import { mapSnakeCaseToCapitalCaseWithSpaces } from "../../../../components/common/utils/StringUtils";

interface UpdateRatecardModalProps {
  clientId: number;
  clientType: string;
  ratecardData: RatecardResponse | null;
  closeUpdateRatecardModal: CallableFunction;
  onCompleted: CallableFunction;
}

interface UpdateRateCardUiRequest {
  isActive: string;
  associations: string[];
}

const UpdateRatecardModal = (props: UpdateRatecardModalProps) => {
  const editInitialValues: UpdateRateCardUiRequest = {
    isActive: props.ratecardData?.isActive ? "YES" : "NO",
    associations: mapClientMappingsResponseToStringList(
      props.ratecardData?.ratecardClientMappings
    ),
  };

  const dispatch = useAppDispatch();

  const [clonedRatecard, setClonedRatecard] = useState<RatecardResponse | null>(
    null
  );

  const [updateRatecardUIRequest, setUpdateRatecardUIRequest] =
    useState(editInitialValues);

  useEffect(() => {
    setClonedRatecard(props.ratecardData);
    if (props.ratecardData?.isActive) {
      fetchStoreRateCardAssociationData();
    }
  }, []);

  // get stores - ratecard association data
  const fetchStoreRateCardAssociationData = async () => {
    try {
      const response = await dispatch(
        fetchStoreRateCardAssociationList(props.clientId)
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message, {
          toastId: "storeRateCardAssociationList",
        });
        return;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "storeRateCardAssociationException",
      });
    } finally {
      // setDataFetchig(false);
    }
  };

  // import store data from "Redux-Store"
  const { isLoading, storeRateCardAssociationList } = useSelector(
    (state: RootState) => state.ratecard
  );

  const parcelCentersRateCardAssociationList: ParcelCenterRateCardAssociationResponse[] =
    []; // TODO: to be implemented for parcel centers
  // const parcelCentersRateCardAssociationList: StoreRateCardAssociationResponse[] = [];

  // validation rule while updating a ratecard
  const validationSchema = Yup.object().shape({
    isActive: Yup.string().required("Please select this field"),
    associations: Yup.array().when("isActive", (isActive, schema) => {
      if (isActive && !props.ratecardData?.isActive)
        return Yup.array().optional();

      return Yup.array()
        .min(1, `Pick at least one item`)
        .of(Yup.string())
        .required("Pick at least one item");
    }),
  });

  const mapUiRequestToServerRequest = (
    uiRequest: UpdateRateCardUiRequest
  ): UpdateRateCardRequest => {
    let serverRequest: UpdateRateCardRequest = {
      isActive: uiRequest.isActive === "YES",
      clientId: props.clientId,
      ratecardClientMappings: [],
      ratecardValues: {
        baseRate: 0,
        variableRate: 0,
        variableRateType: "",
        routeMinimum: 0,
        lateCancellationCharge: 0,
        lateCancellationType: "",
      },
      rateCardCustomFieldsData:[],
      ratecardFuelSurchargeFields: [],
      ratecardFuelSurchargeLookup: [],
      ratecardPeakSurcharges: [],
    };

    let storeMappings: UpdateRatecardClientMapping[] = [];
    let parcelCenterMappings: UpdateRatecardClientMapping[] = [];

    uiRequest.associations.forEach((association) => {
      const storeMapping = getStoreMappingFromStoreName(
        props.clientId,
        association,
        props.ratecardData?.ratecardClientMappings,
        storeRateCardAssociationList
      );
      if (storeMapping) storeMappings.push(storeMapping);
    });

    // TODO: add parcel centers also

    const ratecardClientMappings = storeMappings.concat(parcelCenterMappings);
    serverRequest.ratecardClientMappings = ratecardClientMappings;

    return serverRequest;
  };

  //on form submit to update AddRatecardUIRequest
  const handleSubmit = async (addRatecardDetails: UpdateRateCardUiRequest) => {
    setUpdateRatecardUIRequest(addRatecardDetails);

    const updatedIsActive = addRatecardDetails.isActive === "YES";

    if (
      (props.ratecardData && !props.ratecardData.isActive && updatedIsActive) ||
      (props.ratecardData && props.ratecardData.isActive && !updatedIsActive)
    ) {
      // if ratecard isActive field is changed
      // send request for de-activate/re-activate ratecard
      try {
        const updateRatecardRequest: UpdateRateCardRequest = {
          clientId: props.clientId,
          isActive: updatedIsActive,
          ratecardClientMappings: props.ratecardData.ratecardClientMappings,
          ratecardValues: props.ratecardData.ratecardValues,
          ratecardFuelSurchargeFields:
            props.ratecardData.ratecardFuelSurchargeFields,
          ratecardFuelSurchargeLookup: [],
          ratecardPeakSurcharges: props.ratecardData.ratecardPeakSurcharges,
          rateCardCustomFieldsData:props.ratecardData.rateCardCustomFieldsData,
        };
        const response = await dispatch(
          updateRatecard({
            ratecardId: props.ratecardData.ratecardId,
            updateRatecardData: updateRatecardRequest,
          })
        ).unwrap();
        if (!response.isSuccess) {
          toast.error(response.message, {
            toastId: "updateRatecard",
          });
          toast.success("Ratecard has been updated successfully.");
        }
      } catch (error) {
        const appError = getAxiosError(error);
        if (appError.code === "400") {
          toast.error("Some error occured", {
            toastId: "updateRatecardException",
          });
        } else {
          toast.error(appError.errorMessage, {
            toastId: "updateRatecardException",
          });
        }
      } finally {
        props.onCompleted();
        props.closeUpdateRatecardModal();
      }
    } else {
      openRouteRateModal();
    }

    return true;
  };

  // open  modal logic to open update route rate modal
  const [updateRouteRateModal, openRouteRateModal] = useSubmitModal(
    (closeRouteRateModal) => (
      <UpdateRouteRateModal
        clientId={props.clientId}
        ratecardData={clonedRatecard}
        ratecardRequest={mapUiRequestToServerRequest(updateRatecardUIRequest)}
        closeUpdateRouteRateModal={closeRouteRateModal}
        onCompleted={() => {
          props.closeUpdateRatecardModal();
        }}
      />
    )
  );

  const storeAndParcelCenterAssociationList = storeRateCardAssociationList.map(
    (storeAssociation) => {
      return {
        ...storeAssociation,
        group: "Stores",
        isStore: true,
        nameIdentifier: storeAssociation.storeId, // adding name Identifier as stores and parcel centers don't have a common attribute for the name
      };
    }
  );

  return (
    <>
      <Formik
        initialValues={{ ...editInitialValues }}
        validationSchema={validationSchema}
        onSubmit={(values: UpdateRateCardUiRequest, { setSubmitting }) => {
          setSubmitting(true);
          if (props.ratecardData?.ratecardType === "route_rate") {
            handleSubmit(values);
          } else if (props.ratecardData?.ratecardType === "package_rate") {
            // TODO: add implementation for package_rate
          } else {
            // TODO: add implementation for flat_rate
          }

          setSubmitting(false);
        }}
      >
        {({
          handleChange,
          handleBlur,
          values,
          setFieldValue,
          isSubmitting,
        }) => {
          const isRatecardIsActiveChanged =
            (props.ratecardData &&
              !props.ratecardData.isActive &&
              values.isActive === "YES") ||
            (props.ratecardData &&
              props.ratecardData.isActive &&
              !(values.isActive === "YES"));
          return (
            <SubmitModalWithFormik
              title="Edit Rate Card"
              closeSubmitModal={props.closeUpdateRatecardModal}
              childErrorMessage={""}
              allowSubmit={false}
              size="xs"
              isLoading={false}
              submitButtonName={
                // check if ratecard isActive field is changed
                // handle update request here
                isRatecardIsActiveChanged ? "Update" : "Next"
              }
              closeModalAfterSubmit={false}
              fullWidth={false}
              allowSubmitWithoutTouch={true}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Loader isLoading={isLoading || isSubmitting} />
                <Form>
                  <Grid container>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <TextField
                        margin="normal"
                        id="ratecardName"
                        name="ratecardName"
                        label="Rate Card Name"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        disabled
                        value={props.ratecardData?.ratecardName}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <FormikTextField
                        select
                        margin="normal"
                        id="isActive"
                        name="isActive"
                        label="Is Active"
                        size="small"
                        fullWidth
                        variant={"outlined"}
                        required
                      >
                        {["YES", "NO"].map((isActive, index) => (
                          <MenuItem key={index} value={isActive}>
                            {isActive}
                          </MenuItem>
                        ))}
                      </FormikTextField>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <Autocomplete
                        multiple
                        id="associations"
                        options={storeAndParcelCenterAssociationList}
                        groupBy={(option) => option.group}
                        limitTags={3}
                        disableCloseOnSelect
                        size="small"
                        onChange={(event, value) =>
                          setFieldValue(
                            "associations",
                            value.map((x) => x.nameIdentifier)
                          )
                        }
                        value={storeAndParcelCenterAssociationList.filter((x) =>
                          values.associations.includes(x.nameIdentifier)
                        )}
                        getOptionDisabled={(option) =>
                          option.ratecardId !==
                            props.ratecardData?.ratecardId &&
                          option.isAssociated
                        }
                        getOptionLabel={(option) =>
                          `${option.nameIdentifier} ${
                            option.ratecardId !==
                              props.ratecardData?.ratecardId &&
                            option.isAssociated
                              ? ` - ${option.ratecardName}`
                              : ``
                          }`
                        }
                        sx={{
                          // overring font-family else it chose different font-family on its own!
                          fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
                          display:
                            isRatecardIsActiveChanged ||
                            !props.ratecardData?.isActive
                              ? "none"
                              : "",
                        }}
                        renderInput={(params) => (
                          <FormikTextField
                            {...params}
                            margin="normal"
                            name="associations"
                            label={
                              props.clientType === "both"
                                ? "Associated Parcel Centers/Stores"
                                : props.clientType === "e2e"
                                ? "Associated Parcel Centers"
                                : "Associated Stores"
                            }
                            size="small"
                            fullWidth
                            variant={"outlined"}
                            required
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <TextField
                        select
                        margin="normal"
                        id="ratecardType"
                        name="ratecardType"
                        label="Rate Card Type"
                        size="small"
                        disabled
                        value={props.ratecardData?.ratecardType}
                        fullWidth
                        variant={"outlined"}
                        required
                      >
                        {RATE_CARD_TYPES.map((ratecardType, index) => (
                          <MenuItem key={index} value={ratecardType}>
                            {mapSnakeCaseToCapitalCaseWithSpaces(ratecardType)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Form>
              </Box>
            </SubmitModalWithFormik>
          );
        }}
      </Formik>
      {updateRouteRateModal}
    </>
  );
};

export default UpdateRatecardModal;
